import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Drawer } from "../../../components/Drawer";
import { Spacing } from "../../../theme/Spacing";
import { AtiraTabs } from "../../../components/AtiraTabs";
import { EntryDetailsDrawerInfoTab } from "./entryDetailsDrawerTabs/EntryDetailsDrawerInfoTab";
import { EntryDetailsDrawerTasksTab } from "./entryDetailsDrawerTabs/EntryDetailsDrawerTasksTab";
import { EntryDetailsDrawerModulesTab } from "./EntryDetailsDrawerModulesTab";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { entryActions } from "../../../redux/entry/entry.slice";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { ActivityTab } from "../../../components/activity-timeline/ActivityTab";
import { useDrawersContext } from "../../tasks/drawers-context";
import { useSearchParams } from "react-router-dom";
import { Entry } from "../../../model/entry/Entry";
import { TagList } from "../../../components/TagList";

const StyledTabs = styled(AtiraTabs)`
  padding-inline: 0;
  padding-top: ${Spacing.s};

  .ant-tabs-content,
  .ant-tabs-tabpane-active {
    height: 100%;
  }

  .ant-tabs-nav::before {
    display: none;
  }

  .ant-tabs-nav-list {
    width: 100% !important;
    justify-content: space-between !important;
  }

  .ant-tabs-tab {
    flex-grow: 1;
    justify-content: center;
  }
`;

export const EntryDetailsDrawer: React.FC = () => {
  const { t } = useTranslation();
  const {
    entry: _entry,
    setEntry,
    entryDetailsVisible,
    setEntryDetailsVisible,
  } = useDrawersContext();

  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const lastFetchedIdRef = useRef<string | null>(null);
  const entry =
    useAppSelector((s) =>
      entrySliceSelectors.selectEntryById(s, _entry?._id || ""),
    ) || _entry;

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const moduleDefinitions = useAppSelector(
    moduleSliceSelectors.selectAllModuleDefinitions,
  );

  const items = [
    {
      key: "info",
      label: t("common.info"),
      children: <EntryDetailsDrawerInfoTab />,
    },
    {
      key: "tasks",
      label: t("common.tasks"),
      children: <EntryDetailsDrawerTasksTab />,
    },
    {
      key: "activity",
      label: t("common.activity"),
      children: <ActivityTab entity={entry || {}} />,
    },
    ...(moduleDefinitions?.length
      ? [
          {
            key: "modules",
            label: t("common.modules"),
            children: <EntryDetailsDrawerModulesTab />,
          },
        ]
      : []),
  ];

  const localOnClose = () => {
    setEntryDetailsVisible(false);
    setEntry(null);
    lastFetchedIdRef.current = null;
    if (searchParams.get("previewEntryId")) {
      setSearchParams({});
    }
  };

  useEffect(() => {
    if (!entryDetailsVisible) {
      return;
    }

    if (!entry?._id || lastFetchedIdRef.current === entry._id) {
      return;
    }

    dispatch(entryActions.getEntryById({ entryId: entry?._id!, userId })).then(
      (entryRes) => {
        const fetchedEntry = entryRes.payload as Entry;
        if (fetchedEntry) {
          setEntry(fetchedEntry);
          lastFetchedIdRef.current = entry._id;
        }
      },
    );
  }, [setEntry, dispatch, entry, userId, entryDetailsVisible]);

  if (!entry) {
    return null;
  }

  return (
    <Drawer
      title={t("entries.details.drawer.title")}
      onClose={localOnClose}
      open={entryDetailsVisible}
      size="large"
      destroyOnHidden
    >
      <TagList tagIds={entry.tagIds} wrap />
      <StyledTabs items={items} />
    </Drawer>
  );
};
